import React, { useState, useEffect, useCallback } from "react";
import { Bid, BidSort, AppConfig } from "../types";
import { DropCatchService } from "../services/dropcatchService";

interface BidsManagerProps {
  config: AppConfig;
  dropcatchService: DropCatchService;
}

const BidsManager: React.FC<BidsManagerProps> = ({ dropcatchService }) => {
  const [bids, setBids] = useState<Bid[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<BidSort>("HighBidDesc");
  const [filterWinning, setFilterWinning] = useState<boolean | undefined>(undefined);

  // Bid update state
  const [updatingBid, setUpdatingBid] = useState<number | null>(null);
  const [newBidAmount, setNewBidAmount] = useState<Record<number, string>>({});

  const loadBids = useCallback(async () => {
    if (!dropcatchService.isConfigured()) {
      setError("Please configure your DropCatch API credentials");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await dropcatchService.getBids({
        sort: sortBy,
        winning: filterWinning,
        size: 100,
      });
      setBids(result.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load bids");
    } finally {
      setLoading(false);
    }
  }, [dropcatchService, sortBy, filterWinning]);

  useEffect(() => {
    if (dropcatchService.isConfigured()) {
      loadBids();
    }
  }, [sortBy, filterWinning]);

  const handleUpdateBid = async (bid: Bid) => {
    const amount = parseFloat(newBidAmount[bid.auctionId] || "0");
    if (!amount || amount <= bid.highBid) {
      setError(`New bid must be higher than current high bid ($${bid.highBid})`);
      return;
    }

    setUpdatingBid(bid.auctionId);
    setError(null);

    try {
      const result = await dropcatchService.placeBids([
        { auctionId: bid.auctionId, amount: amount },
      ]);

      if (result.successes?.length) {
        loadBids();
        setNewBidAmount((prev) => ({ ...prev, [bid.auctionId]: "" }));
      }

      if (result.failures?.length) {
        setError(result.failures[0].error?.description || "Failed to update bid");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update bid");
    } finally {
      setUpdatingBid(null);
    }
  };

  const winningBids = bids.filter((b) => b.isWinning);
  const losingBids = bids.filter((b) => !b.isWinning);

  return (
    <div className="p-6 space-y-6 font-mono">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1
            className="text-2xl font-bold text-green-400 tracking-wider"
            style={{ textShadow: "0 0 10px rgba(34, 197, 94, 0.5)" }}
          >
            {">"} MY_BIDS.EXE
          </h1>
          <p className="text-green-600 text-sm mt-1">
            TRACK AND MANAGE AUCTION BIDS
          </p>
        </div>
        <button
          onClick={loadBids}
          disabled={loading}
          className="px-4 py-2 bg-green-500 text-black font-bold tracking-wider hover:bg-green-400 transition-colors disabled:opacity-50 flex items-center gap-2"
          style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.3)" }}
        >
          {loading ? (
            <span className="animate-pulse">LOADING...</span>
          ) : (
            "[REFRESH]"
          )}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div
          className="bg-black border-2 border-green-700 p-4"
          style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.1)" }}
        >
          <div className="text-green-600 text-xs mb-1">TOTAL_BIDS</div>
          <div
            className="text-3xl font-bold text-green-400"
            style={{ textShadow: "0 0 10px rgba(34, 197, 94, 0.5)" }}
          >
            {bids.length}
          </div>
          <div className="text-green-700 text-xs mt-1">ACTIVE</div>
        </div>

        <div
          className="bg-black border-2 border-green-700 p-4"
          style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.1)" }}
        >
          <div className="text-green-600 text-xs mb-1">WINNING</div>
          <div
            className="text-3xl font-bold text-green-400"
            style={{ textShadow: "0 0 10px rgba(34, 197, 94, 0.5)" }}
          >
            {winningBids.length}
          </div>
          <div className="text-green-700 text-xs mt-1">LEADING</div>
        </div>

        <div
          className="bg-black border-2 border-green-700 p-4"
          style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.1)" }}
        >
          <div className="text-green-600 text-xs mb-1">OUTBID</div>
          <div
            className="text-3xl font-bold text-green-500"
            style={{ textShadow: "0 0 10px rgba(34, 197, 94, 0.5)" }}
          >
            {losingBids.length}
          </div>
          <div className="text-green-700 text-xs mt-1">BEHIND</div>
        </div>

        <div
          className="bg-black border-2 border-green-700 p-4"
          style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.1)" }}
        >
          <div className="text-green-600 text-xs mb-1">TOTAL_RISK</div>
          <div
            className="text-3xl font-bold text-green-400"
            style={{ textShadow: "0 0 10px rgba(34, 197, 94, 0.5)" }}
          >
            ${bids.reduce((sum, b) => sum + b.highBid, 0).toLocaleString()}
          </div>
          <div className="text-green-700 text-xs mt-1">USD</div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-black border-2 border-green-500 p-4 flex items-start gap-3">
          <span className="text-green-500">[ERR!]</span>
          <p className="text-green-400 flex-1">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-green-600 hover:text-green-400"
          >
            [X]
          </button>
        </div>
      )}

      {/* Filters */}
      <div
        className="bg-black border-2 border-green-700 p-4"
        style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.1)" }}
      >
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex gap-2">
            <button
              onClick={() => setFilterWinning(undefined)}
              className={`px-3 py-1 border-2 transition-colors tracking-wider text-sm ${
                filterWinning === undefined
                  ? "border-green-500 bg-green-500 text-black"
                  : "border-green-700 text-green-600 hover:border-green-500"
              }`}
            >
              [ALL]
            </button>
            <button
              onClick={() => setFilterWinning(true)}
              className={`px-3 py-1 border-2 transition-colors tracking-wider text-sm ${
                filterWinning === true
                  ? "border-green-500 bg-green-500 text-black"
                  : "border-green-700 text-green-600 hover:border-green-500"
              }`}
            >
              [WINNING]
            </button>
            <button
              onClick={() => setFilterWinning(false)}
              className={`px-3 py-1 border-2 transition-colors tracking-wider text-sm ${
                filterWinning === false
                  ? "border-green-500 bg-green-500 text-black"
                  : "border-green-700 text-green-600 hover:border-green-500"
              }`}
            >
              [OUTBID]
            </button>
          </div>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as BidSort)}
            className="bg-black border-2 border-green-700 text-green-400 px-3 py-1 focus:border-green-500 outline-none text-sm"
          >
            <option value="HighBidDesc">HIGHEST BID FIRST</option>
            <option value="HighBidAsc">LOWEST BID FIRST</option>
            <option value="MaxBidDesc">HIGHEST MAX BID</option>
            <option value="DomainAsc">DOMAIN A-Z</option>
          </select>
        </div>
      </div>

      {/* Bids Table */}
      <div
        className="bg-black border-2 border-green-700 overflow-hidden"
        style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.1)" }}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b-2 border-green-700">
                <th className="px-4 py-3 text-xs font-bold text-green-500 uppercase tracking-wider">
                  DOMAIN
                </th>
                <th className="px-4 py-3 text-xs font-bold text-green-500 uppercase tracking-wider">
                  STATUS
                </th>
                <th className="px-4 py-3 text-xs font-bold text-green-500 uppercase tracking-wider">
                  HIGH_BID
                </th>
                <th className="px-4 py-3 text-xs font-bold text-green-500 uppercase tracking-wider">
                  YOUR_MAX
                </th>
                <th className="px-4 py-3 text-xs font-bold text-green-500 uppercase tracking-wider text-right">
                  ACTIONS
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-green-900">
              {bids.length === 0 && !loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center">
                    <pre className="text-green-700 text-xs mb-4">
{`    ____________________
   |                    |
   |   NO ACTIVE BIDS   |
   |____________________|
   |____________________|`}
                    </pre>
                    <p className="text-green-600 text-sm">
                      BROWSE AUCTIONS TO PLACE YOUR FIRST BID
                    </p>
                  </td>
                </tr>
              ) : (
                bids.map((bid) => (
                  <tr
                    key={bid.auctionId}
                    className="hover:bg-green-900/20 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <a
                        href={`https://www.dropcatch.com/domain/${bid.domain}`}
                        target="_blank"
                        rel="noreferrer"
                        className="font-bold text-green-400 hover:text-green-300 transition-colors"
                        style={{ textShadow: "0 0 5px rgba(34, 197, 94, 0.3)" }}
                      >
                        {bid.domain}
                      </a>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 border text-xs font-bold tracking-wider ${
                          bid.isWinning
                            ? "border-green-500 text-green-400"
                            : "border-green-700 text-green-600"
                        }`}
                      >
                        {bid.isWinning ? "[WINNING]" : "[OUTBID]"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="font-bold text-green-400"
                        style={{ textShadow: "0 0 5px rgba(34, 197, 94, 0.3)" }}
                      >
                        ${bid.highBid.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-green-500">
                        ${bid.maxBid.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end items-center gap-2">
                        {!bid.isWinning && (
                          <>
                            <input
                              type="number"
                              placeholder={`$${bid.highBid + 1}`}
                              value={newBidAmount[bid.auctionId] || ""}
                              onChange={(e) =>
                                setNewBidAmount((prev) => ({
                                  ...prev,
                                  [bid.auctionId]: e.target.value,
                                }))
                              }
                              className="w-24 px-2 py-1 bg-black border-2 border-green-700 text-green-400 text-sm focus:border-green-500 outline-none placeholder-green-800"
                            />
                            <button
                              onClick={() => handleUpdateBid(bid)}
                              disabled={updatingBid === bid.auctionId}
                              className="px-3 py-1 bg-green-500 text-black font-bold text-xs tracking-wider hover:bg-green-400 transition-colors disabled:opacity-50"
                            >
                              {updatingBid === bid.auctionId ? "..." : "[RAISE]"}
                            </button>
                          </>
                        )}
                        <a
                          href={`https://www.dropcatch.com/domain/${bid.domain}`}
                          target="_blank"
                          rel="noreferrer"
                          className="px-2 py-1 text-green-600 hover:text-green-400 transition-colors text-xs"
                        >
                          [VIEW]
                        </a>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Table Footer */}
        <div className="border-t-2 border-green-700 px-4 py-2 text-xs text-green-700 flex justify-between">
          <span>RECORDS: {bids.length}</span>
          <span className="animate-pulse">SYSTEM READY_</span>
        </div>
      </div>
    </div>
  );
};

export default BidsManager;
